/**
 * @file pages/well/WellHomePage.js
 * @description Well Butler — Home / job-start launcher (plan 06 §C.2 #1).
 *
 * Phone-first, big tap targets. Four primary tools + History. This is the screen
 * the field tech lands on; everything is one thumb-tap away.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../../design-system';

const TOOLS = [
  {
    to: '/well/size',
    emoji: '📏',
    title: 'Size a pump',
    blurb: 'Pick the right pump (horsepower + flow) for this well and house.',
  },
  {
    to: '/well/megtest',
    emoji: '\u26a1',
    title: 'Meg test calculator',
    blurb: 'Punch in megger readings from any test point \u2014 pass/fail per reading, plus which section is bad.',
  },
  {
    to: '/well/diagnose',
    emoji: '🔎',
    title: 'Diagnose / troubleshoot',
    blurb: 'Answer a few field readings and get the most-likely cause, ranked.',
  },
  {
    to: '/well/audit',
    emoji: '📷',
    title: 'Photo audit',
    blurb: 'Snap the wellhead — get the parts list and safety flags.',
  },
  {
    to: '/well/trench',
    emoji: '⛏️',
    title: 'Estimate a trench',
    blurb: 'Price a waterline or well + power run — hand dig vs. trencher.',
  },
  {
    to: '/well/history',
    emoji: '🕑',
    title: 'History',
    blurb: 'Recent results from this device.',
  },
  {
    to: '/well/summarize',
    emoji: '📄',
    title: 'Summarize a report',
    blurb: 'Upload any inspection report PDF — get a one-page plain-English summary in front of it.',
  },
  // WHY (2026-10-07, James): defect #173 — this tile leaves the app for a web
  // page (the public page from defect 4). Until James decides whether that page
  // stays public or moves inside the app, the tile says plainly where it goes.
  {
    href: 'https://butlercore.com/howto/nc-septic-reference.html',
    emoji: '📖',
    title: 'NC Septic Reference',
    blurb: 'Setbacks, tank sizing, and code numbers — the NC septic rules on one page. '
      + 'Opens a web page in a new tab.',
  },
];

export default function WellHomePage() {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 560, width: '100%', margin: '0 auto', padding: 'var(--space-4) var(--space-3) var(--space-10)' }}>
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <Link to="/" style={{ color: 'var(--color-primary)', fontSize: 'var(--text-body-sm)', textDecoration: 'none' }}>
          ← Apps
        </Link>
      </div>
      <h1 style={{ fontSize: 'var(--text-heading-lg, 1.6rem)', fontWeight: 'var(--weight-bold)', margin: '0 0 var(--space-1)' }}>
        Well &amp; Septic Butler
      </h1>
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 var(--space-5)', lineHeight: 1.5 }}>
        Your field sidekick for submersible well pumps — plus the NC septic reference.
        Every answer shows how sure it is — and reminds you that the final call is yours.
      </p>

      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {TOOLS.map((t) => (
          <Card
            key={t.to || t.href}
            variant="interactive"
            padding="md"
            onClick={() => (t.href ? window.open(t.href, '_blank', 'noopener') : navigate(t.to))}
            aria-label={t.title}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div style={{ fontSize: '2rem', lineHeight: 1 }} aria-hidden="true">{t.emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-body-lg, 1.1rem)', fontWeight: 'var(--weight-bold)' }}>{t.title}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginTop: 2 }}>{t.blurb}</div>
              </div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: '1.4rem' }} aria-hidden="true">›</div>
            </div>
          </Card>
        ))}
      </div>

      <p style={{ marginTop: 'var(--space-6)', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', lineHeight: 1.5 }}>
        Well &amp; Septic Butler gives you a guided best-guess from field data — never a final verdict.
        You're the licensed pro. Always verify before you pull a pump, order parts, or condemn equipment.
      </p>
    </div>
  );
}
